// frontend/src/components/admin/BillingConfig.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Bell, Clock, Plus, Trash2, Edit3, Save, X,
  ChevronDown, ChevronUp, AlertCircle, CheckCircle2,
  Calendar, Building2, Zap, Info, DollarSign, Percent, Tag, CreditCard
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// CONSTANTES
// ============================================================================

const VARIABLES = [
  { key: '{company_name}',  label: 'Nom entreprise' },
  { key: '{billing_day}',   label: 'Jour de facturation' },
  { key: '{days_left}',     label: 'Jours restants' },
  { key: '{echeance_date}', label: 'Date échéance' },
];

const COLOR_OPTIONS = [
  { label: 'Ambre',  value: '#f59e0b', gradient: 'from-amber-500 to-orange-500' },
  { label: 'Rouge',  value: '#ef4444', gradient: 'from-red-500 to-rose-600' },
  { label: 'Bleu',   value: '#3b82f6', gradient: 'from-blue-500 to-indigo-500' },
  { label: 'Violet', value: '#8b5cf6', gradient: 'from-violet-500 to-purple-600' },
  { label: 'Vert',   value: '#10b981', gradient: 'from-emerald-500 to-teal-500' },
];

const DEFAULT_REMINDER = {
  days_before: 7,
  titre: 'Rappel : votre paiement arrive dans {days_left} jours',
  contenu: 'Bonjour {company_name},\n\nVotre prochaine échéance de facturation est prévue le {echeance_date} (dans {days_left} jours).\n\nMerci de vous assurer que votre paiement est en ordre.',
  badge: 'Rappel facturation',
  badge_color: '#f59e0b',
  gradient: 'from-amber-500 to-orange-500',
  icon_name: 'Clock',
  is_active: true,
};

const DISCOUNT_TYPES = [
  { value: 'percent', label: 'Pourcentage (%)'     },
  { value: 'fixed',   label: 'Montant fixe (FCFA)' },
];

// ============================================================================
// COMPOSANT MODAL RAPPEL
// ============================================================================

const ReminderModal = ({ reminder, companyName, onSave, onClose, theme }) => {
  const [form, setForm] = useState(reminder || { ...DEFAULT_REMINDER });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const insertVariable = (textarea, variable, field) => {
    const el = document.getElementById(textarea);
    if (!el) { set(field, form[field] + variable); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newVal = form[field].substring(0, start) + variable + form[field].substring(end);
    set(field, newVal);
    setTimeout(() => { el.selectionStart = el.selectionEnd = start + variable.length; el.focus(); }, 0);
  };

  const handleSave = async () => {
    if (!form.titre.trim() || !form.contenu.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: theme.bgOverlay }}>
      <div className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: theme.border }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: theme.primarySoft }}>
              <Bell size={18} style={{ color: theme.primary }} />
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ color: theme.text }}>
                {reminder ? 'Modifier le rappel' : 'Nouveau rappel'}
              </p>
              <p className="text-xs" style={{ color: theme.textMuted }}>{companyName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: theme.textMuted }}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
              Déclencher combien de jours avant l'échéance ?
            </label>
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={30} value={form.days_before}
                onChange={e => set('days_before', parseInt(e.target.value) || 1)}
                className="w-24 px-3 py-2 rounded-lg text-sm font-bold text-center"
                style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
              />
              <span className="text-sm" style={{ color: theme.textMuted }}>jours avant le jour de facturation</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>Badge / étiquette</label>
              <input type="text" value={form.badge} onChange={e => set('badge', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>Couleur</label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_OPTIONS.map(c => (
                  <button key={c.value} onClick={() => { set('badge_color', c.value); set('gradient', c.gradient); }}
                    className="w-7 h-7 rounded-full border-2 transition-transform"
                    style={{ background: c.value, borderColor: form.badge_color === c.value ? theme.text : 'transparent', transform: form.badge_color === c.value ? 'scale(1.2)' : 'scale(1)' }}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block" style={{ color: theme.textSecondary }}>Titre de la notification</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {VARIABLES.map(v => (
                <button key={v.key} onClick={() => insertVariable('input-titre', v.key, 'titre')}
                  className="text-xs px-2 py-1 rounded-md font-mono"
                  style={{ background: theme.primarySoft, color: theme.primary }}>{v.key}</button>
              ))}
            </div>
            <input id="input-titre" type="text" value={form.titre} onChange={e => set('titre', e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
            />
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block" style={{ color: theme.textSecondary }}>Corps du message</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {VARIABLES.map(v => (
                <button key={v.key} onClick={() => insertVariable('textarea-contenu', v.key, 'contenu')}
                  className="text-xs px-2 py-1 rounded-md font-mono"
                  style={{ background: theme.primarySoft, color: theme.primary }}>{v.key}</button>
              ))}
            </div>
            <textarea id="textarea-contenu" rows={5} value={form.contenu} onChange={e => set('contenu', e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm resize-none"
              style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
            />
          </div>

          <div className="p-3 rounded-xl text-xs" style={{ background: theme.primarySofter, border: `1px solid ${theme.primaryBorder}` }}>
            <p className="font-semibold mb-1" style={{ color: theme.primary }}>Variables disponibles :</p>
            <div className="space-y-1">
              {VARIABLES.map(v => (
                <div key={v.key} className="flex gap-2">
                  <code className="font-mono" style={{ color: theme.primary }}>{v.key}</code>
                  <span style={{ color: theme.textSecondary }}>→ {v.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => set('is_active', !form.is_active)}
              className="relative w-11 h-6 rounded-full transition-colors"
              style={{ background: form.is_active ? theme.primary : theme.border }}>
              <span className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: form.is_active ? '1.375rem' : '0.25rem' }} />
            </button>
            <span className="text-sm" style={{ color: theme.textSecondary }}>
              {form.is_active ? 'Rappel actif' : 'Rappel désactivé'}
            </span>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t" style={{ borderColor: theme.border }}>
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-sm font-medium"
            style={{ background: theme.bgInput, color: theme.textSecondary }}>Annuler</button>
          <button onClick={handleSave} disabled={saving || !form.titre.trim() || !form.contenu.trim()}
            className="flex-1 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: theme.primary, color: '#fff' }}>
            {saving ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={15} />}
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT LIGNE ENTREPRISE
// ============================================================================

const CompanyRow = ({ company, theme, onRefresh }) => {
  const [expanded, setExpanded]     = useState(false);
  const [reminders, setReminders]   = useState([]);
  const [billingDay, setBillingDay] = useState(company.billing_day || '');
  const [loading, setLoading]       = useState(false);
  const [modal, setModal]           = useState(null);
  const [savingDay, setSavingDay]   = useState(false);

  const [pricing, setPricing] = useState({
    interlocuteur:  company.interlocuteur  || '',
    discount_type:  company.discount_type  || 'percent',
    discount_value: company.discount_value || 0,
  });
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingOk,     setPricingOk]     = useState(false);

  const [prixBase,    setPrixBase]    = useState(null);
  const [typeLicence, setTypeLicence] = useState('');

  // ── Extensions actives de cette entreprise ──────────────────────────────
  // Chaque item : { flag_key, label, prix_mensuel }
  const [extensions,     setExtensions]     = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [addons,         setAddons]         = useState({ employes: 0, utilisateurs: 0, prixEmp: 2500, prixUsr: 3500 });

  // ── Calculs ─────────────────────────────────────────────────────────────
  const prixLicenceFinal = () => {
    if (prixBase === null) return null;
    const base = parseFloat(prixBase) || 0;
    const val  = parseFloat(pricing.discount_value) || 0;
    if (pricing.discount_type === 'percent')
      return Math.max(0, base * (1 - val / 100));
    return Math.max(0, base - val);
  };

  const totalExtensions = extensions.reduce(
    (sum, e) => sum + (parseFloat(e.prix_mensuel) || 0), 0
  );

  const totalAddons = (addons.utilisateurs * addons.prixUsr)
                    + (Math.ceil(addons.employes / 10) * addons.prixEmp);

  const totalAPayer = () => {
    const lic = prixLicenceFinal();
    if (lic === null) return null;
    return lic + totalExtensions + totalAddons;
  };

  const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR') + ' FCFA';

  // ── Chargement ──────────────────────────────────────────────────────────
  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await superAdminApi.billing.updatePricing(company.id, pricing);
      setPricingOk(true);
      setTimeout(() => setPricingOk(false), 2500);
    } finally {
      setSavingPricing(false);
    }
  };

  const loadReminders = useCallback(async () => {
    if (!expanded) return;
    setLoading(true);
    try {
      const res = await superAdminApi.billingReminders.getByCompany(company.id);
      if (res.success) {
        setReminders(res.data.reminders);
        setBillingDay(res.data.billing_day || '');
      }

      // Prix de base + discount
      try {
        const pRes = await superAdminApi.billing.getPrixBase(company.id);
        if (pRes.success && pRes.data) {
          setPrixBase(pRes.data.prix || 0);
          setTypeLicence(pRes.data.type_licence || '');
          setPricing(prev => ({
            ...prev,
            interlocuteur:  pRes.data.interlocuteur  || '',
            discount_type:  pRes.data.discount_type  || 'percent',
            discount_value: pRes.data.discount_value || 0,
          }));
          // Add-ons
          setAddons({
            employes:     parseInt(pRes.data?.addon_employes)             || 0,
            utilisateurs: parseInt(pRes.data?.addon_utilisateurs)         || 0,
            prixEmp:      parseFloat(pRes.data?.prix_par_employe_sup)     || 2500,
            prixUsr:      parseFloat(pRes.data?.prix_par_utilisateur_sup) || 3500,
          });
        }
      } catch (e) {}

      // Extensions actives de cette entreprise
      try {
        setLoadingBilling(true);
        const bRes = await superAdminApi.extensions.getByCompany(company.id);
        if (bRes.success) {
          setExtensions((bRes.data || []).filter(e => e.is_enabled == 1));
        }
      } catch (e) {
        setExtensions([]);
      } finally {
        setLoadingBilling(false);
      }

    } finally {
      setLoading(false);
    }
  }, [expanded, company.id]);

  useEffect(() => { loadReminders(); }, [loadReminders]);

  const handleSaveDay = async () => {
    setSavingDay(true);
    try {
      await superAdminApi.billingReminders.updateBillingDay(company.id, billingDay || null);
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveReminder = async (form) => {
    if (modal === 'new') {
      await superAdminApi.billingReminders.create(company.id, form);
    } else {
      await superAdminApi.billingReminders.update(modal.id, form);
    }
    setModal(null);
    loadReminders();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce rappel ?')) return;
    await superAdminApi.billingReminders.delete(id);
    loadReminders();
  };

  const toggleReminder = async (r) => {
    await superAdminApi.billingReminders.update(r.id, { ...r, is_active: !r.is_active });
    loadReminders();
  };

  return (
    <>
      {modal && (
        <ReminderModal
          reminder={modal === 'new' ? null : modal}
          companyName={company.company_name}
          onSave={handleSaveReminder}
          onClose={() => setModal(null)}
          theme={theme}
        />
      )}

      <div className="rounded-xl overflow-hidden mb-3" style={{ border: `1px solid ${theme.border}`, background: theme.bgCard }}>

        {/* Header entreprise */}
        <div
          className="flex items-center gap-4 p-4 cursor-pointer transition-colors"
          style={{ background: expanded ? theme.primarySofter : 'transparent' }}
          onClick={() => setExpanded(e => !e)}
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: theme.primarySoft }}>
            <Building2 size={16} style={{ color: theme.primary }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate" style={{ color: theme.text }}>
              {company.company_name}
            </p>
            <p className="text-xs" style={{ color: theme.textMuted }}>
              {company.billing_day
                ? `Facturation le ${company.billing_day} du mois`
                : 'Jour de facturation non configuré'}
              {reminders.length > 0 && ` · ${reminders.length} rappel(s)`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {reminders.filter(r => r.is_active).length > 0 && (
              <span className="text-xs px-2 py-1 rounded-full font-medium"
                style={{ background: theme.successSoft, color: theme.successText }}>
                {reminders.filter(r => r.is_active).length} actif(s)
              </span>
            )}
            {expanded
              ? <ChevronUp size={16} style={{ color: theme.textMuted }} />
              : <ChevronDown size={16} style={{ color: theme.textMuted }} />}
          </div>
        </div>

        {/* Contenu déployé */}
        {expanded && (
          <div className="border-t p-4 space-y-4" style={{ borderColor: theme.border }}>

            {/* Jour de facturation */}
            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold w-40 flex-shrink-0" style={{ color: theme.textSecondary }}>
                Jour de facturation (1-31)
              </label>
              <input type="number" min={1} max={31} value={billingDay}
                onChange={e => setBillingDay(e.target.value)} placeholder="Ex: 15"
                className="w-24 px-3 py-1.5 rounded-lg text-sm text-center font-bold"
                style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
              />
              <button onClick={handleSaveDay} disabled={savingDay}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: theme.primary, color: '#fff' }}>
                {savingDay
                  ? <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                  : <Save size={12} />}
                Sauvegarder
              </button>
            </div>

            {/* ── PRICING & INTERLOCUTEUR ── */}
            <div className="p-4 rounded-xl space-y-4" style={{ background: theme.bgInput, border: `1px solid ${theme.border}` }}>
              <p className="text-xs font-bold flex items-center gap-2" style={{ color: theme.textSecondary }}>
                <CreditCard size={13} /> Tarification & Contact
              </p>

              {/* Prix de base (lecture seule) */}
              {prixBase !== null && (
                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold w-40 flex-shrink-0" style={{ color: theme.textSecondary }}>
                    Prix licence ({typeLicence || company.type_licence || '—'})
                  </label>
                  <span className="text-sm font-bold" style={{ color: theme.text }}>{fmt(prixBase)}</span>
                  <span className="text-xs" style={{ color: theme.textMuted }}>(défini dans les tarifs globaux)</span>
                </div>
              )}

              {/* Interlocuteur */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold w-40 flex-shrink-0" style={{ color: theme.textSecondary }}>
                  Interlocuteur client
                </label>
                <input type="text" value={pricing.interlocuteur}
                  onChange={e => setPricing(p => ({ ...p, interlocuteur: e.target.value }))}
                  placeholder="Ex: Jean Dupont"
                  className="flex-1 px-3 py-1.5 rounded-lg text-sm"
                  style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.text }}
                />
              </div>

              {/* Réduction sur la licence uniquement */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold w-40 flex-shrink-0" style={{ color: theme.textSecondary }}>
                  Réduction (licence)
                </label>
                <select value={pricing.discount_type}
                  onChange={e => setPricing(p => ({ ...p, discount_type: e.target.value }))}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.text }}>
                  {DISCOUNT_TYPES.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                <input type="number" min={0} value={pricing.discount_value}
                  onChange={e => setPricing(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))}
                  className="w-28 px-3 py-1.5 rounded-lg text-sm text-center font-bold"
                  style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.text }}
                />
                <span className="text-xs font-bold" style={{ color: theme.textMuted }}>
                  {pricing.discount_type === 'percent' ? '%' : 'FCFA'}
                </span>
              </div>

              {/* ── Récapitulatif mensuel détaillé ── */}
              {prixLicenceFinal() !== null && (
                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>

                  {/* Titre */}
                  <div className="px-4 py-2.5 flex items-center gap-2"
                    style={{ background: theme.primarySofter, borderBottom: `1px solid ${theme.primaryBorder}` }}>
                    <Zap size={13} style={{ color: theme.primary }} />
                    <span className="text-xs font-bold" style={{ color: theme.primary }}>
                      Récapitulatif mensuel
                    </span>
                    {loadingBilling && (
                      <span className="ml-auto animate-spin w-3 h-3 border-2 rounded-full"
                        style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
                    )}
                  </div>

                  <div className="px-4 py-3 space-y-2" style={{ background: theme.bgCard }}>

                    {/* Licence brute */}
                    <div className="flex justify-between items-center text-xs">
                      <span style={{ color: theme.textSecondary }}>
                        Licence {typeLicence || company.type_licence || ''}
                      </span>
                      <span className="font-semibold" style={{ color: theme.text }}>{fmt(prixBase)}</span>
                    </div>

                    {/* Réduction si applicable */}
                    {(parseFloat(pricing.discount_value) || 0) > 0 && (
                      <>
                        <div className="flex justify-between items-center text-xs">
                          <span style={{ color: '#f59e0b' }}>
                            Réduction {pricing.discount_type === 'percent'
                              ? `(${pricing.discount_value}%)`
                              : '(montant fixe)'}
                          </span>
                          <span className="font-semibold" style={{ color: '#f59e0b' }}>
                            − {fmt(
                              pricing.discount_type === 'percent'
                                ? (prixBase * pricing.discount_value / 100)
                                : pricing.discount_value
                            )}
                          </span>
                        </div>
                        {/* Licence après réduction — séparé visuellement */}
                        <div className="flex justify-between items-center text-xs pb-2"
                          style={{ borderBottom: `1px dashed ${theme.border}` }}>
                          <span className="font-medium" style={{ color: theme.textSecondary }}>
                            Prix licence final
                          </span>
                          <span className="font-bold" style={{ color: theme.text }}>
                            {fmt(prixLicenceFinal())}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Extensions actives */}
                    {!loadingBilling && extensions.length > 0 && extensions.map(ext => (
                      <div key={ext.flag_key} className="flex justify-between items-center text-xs">
                        <span className="flex items-center gap-1.5" style={{ color: theme.textSecondary }}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ background: '#f97316' }} />
                          {ext.label || ext.flag_key}
                        </span>
                        <span className="font-semibold" style={{ color: '#f97316' }}>
                          + {fmt(ext.prix_mensuel)}
                        </span>
                      </div>
                    ))}

                    {/* Aucune extension */}
                    {!loadingBilling && extensions.length === 0 && (
                      <div className="text-xs italic" style={{ color: theme.textMuted }}>
                        Aucune extension active
                      </div>
                    )}

                    {/* Add-ons utilisateurs */}
                    {!loadingBilling && addons.utilisateurs > 0 && (
                      <div className="flex justify-between items-center text-xs">
                        <span className="flex items-center gap-1.5" style={{ color: theme.textSecondary }}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#f59e0b' }} />
                          Add-on {addons.utilisateurs} utilisateur{addons.utilisateurs > 1 ? 's' : ''} sup.
                        </span>
                        <span className="font-semibold" style={{ color: '#f59e0b' }}>
                          + {fmt(addons.utilisateurs * addons.prixUsr)}
                        </span>
                      </div>
                    )}

                    {/* Add-ons employés */}
                    {!loadingBilling && addons.employes > 0 && (
                      <div className="flex justify-between items-center text-xs">
                        <span className="flex items-center gap-1.5" style={{ color: theme.textSecondary }}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#f59e0b' }} />
                          Add-on {addons.employes} employé{addons.employes > 1 ? 's' : ''} sup.
                        </span>
                        <span className="font-semibold" style={{ color: '#f59e0b' }}>
                          + {fmt(Math.ceil(addons.employes / 10) * addons.prixEmp)}
                        </span>
                      </div>
                    )}

                    {/* Total final */}
                    <div className="flex justify-between items-center pt-2"
                      style={{ borderTop: `2px solid ${theme.border}` }}>
                      <span className="text-xs font-bold" style={{ color: theme.textSecondary }}>
                        Total à payer / mois
                      </span>
                      <span className="text-base font-bold" style={{ color: theme.primary }}>
                        {fmt(totalAPayer())}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Bouton sauvegarder */}
              <div className="flex justify-end pt-1">
                <button onClick={handleSavePricing} disabled={savingPricing}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                  style={{ background: theme.primary, color: '#fff' }}>
                  {savingPricing
                    ? <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                    : pricingOk ? <CheckCircle2 size={13} /> : <Save size={13} />}
                  {pricingOk ? 'Enregistré !' : 'Sauvegarder'}
                </button>
              </div>
            </div>

            {/* Liste des rappels */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold" style={{ color: theme.textSecondary }}>
                  Rappels configurés
                </p>
                <button onClick={() => setModal('new')}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold"
                  style={{ background: theme.primarySoft, color: theme.primary }}>
                  <Plus size={13} /> Ajouter un rappel
                </button>
              </div>

              {loading && (
                <div className="flex justify-center py-6">
                  <span className="animate-spin w-5 h-5 border-2 rounded-full"
                    style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
                </div>
              )}

              {!loading && reminders.length === 0 && (
                <div className="text-center py-6 rounded-xl" style={{ background: theme.bgInput }}>
                  <Clock size={24} style={{ color: theme.textMuted }} className="mx-auto mb-2" />
                  <p className="text-xs" style={{ color: theme.textMuted }}>Aucun rappel configuré</p>
                  <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
                    Ajoutez des rappels à J-10, J-5, J-2, J-1...
                  </p>
                </div>
              )}

              {!loading && reminders.length > 0 && (
                <div className="space-y-2">
                  {reminders.sort((a, b) => b.days_before - a.days_before).map(r => (
                    <div key={r.id}
                      className="flex items-start gap-3 p-3 rounded-xl"
                      style={{ background: theme.bgInput, border: `1px solid ${r.is_active ? theme.primaryBorder : theme.border}` }}>
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                        style={{ background: r.badge_color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-md"
                            style={{ background: r.badge_color + '22', color: r.badge_color }}>
                            J-{r.days_before}
                          </span>
                          <p className="text-sm font-medium truncate" style={{ color: theme.text }}>{r.titre}</p>
                        </div>
                        <p className="text-xs truncate" style={{ color: theme.textMuted }}>
                          {r.contenu.substring(0, 80)}...
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => toggleReminder(r)}
                          className="relative w-8 h-4 rounded-full transition-colors"
                          style={{ background: r.is_active ? theme.primary : theme.border }}
                          title={r.is_active ? 'Désactiver' : 'Activer'}>
                          <span className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all"
                            style={{ left: r.is_active ? '1rem' : '0.125rem' }} />
                        </button>
                        <button onClick={() => setModal(r)}
                          className="p-1.5 rounded-lg" style={{ color: theme.primary }} title="Modifier">
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => handleDelete(r.id)}
                          className="p-1.5 rounded-lg" style={{ color: theme.danger }} title="Supprimer">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const BillingConfig = () => {
  const theme = useTheme();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await superAdminApi.companies.getAll();
        if (res.success) {
          setCompanies(res.data.filter(c => c.status !== 'inactive'));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = companies.filter(c =>
    c.company_name.toLowerCase().includes(search.toLowerCase())
  );

  const withBillingDay = companies.filter(c => c.billing_day).length;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold" style={{ color: theme.text }}>
          Configuration facturation
        </h1>
        <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
          Configurez les rappels de paiement progressifs par entreprise (J-10, J-5, J-2, J-1...)
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Entreprises actives', value: companies.length,                  icon: Building2,   color: theme.primary },
          { label: 'Avec jour configuré', value: withBillingDay,                    icon: Calendar,    color: theme.success },
          { label: 'Sans configuration',  value: companies.length - withBillingDay, icon: AlertCircle, color: theme.warning },
        ].map((s, i) => (
          <div key={i} className="p-4 rounded-xl" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: s.color + '20' }}>
                <s.icon size={18} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: theme.text }}>{s.value}</p>
                <p className="text-xs" style={{ color: theme.textMuted }}>{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-3 p-4 rounded-xl"
        style={{ background: theme.primarySofter, border: `1px solid ${theme.primaryBorder}` }}>
        <Info size={16} style={{ color: theme.primary }} className="mt-0.5 flex-shrink-0" />
        <div className="text-xs" style={{ color: theme.textSecondary }}>
          <strong style={{ color: theme.primary }}>Variables dynamiques</strong> disponibles dans les messages :
          {' '}<code style={{ color: theme.primary }}>{'{company_name}'}</code>,
          {' '}<code style={{ color: theme.primary }}>{'{billing_day}'}</code>,
          {' '}<code style={{ color: theme.primary }}>{'{days_left}'}</code>,
          {' '}<code style={{ color: theme.primary }}>{'{echeance_date}'}</code>
        </div>
      </div>

      <input type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Rechercher une entreprise..."
        className="w-full px-4 py-2.5 rounded-xl text-sm"
        style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <span className="animate-spin w-8 h-8 border-2 rounded-full"
            style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <div>
          {filtered.map(company => (
            <CompanyRow key={company.id} company={company} theme={theme} onRefresh={() => {}} />
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12" style={{ color: theme.textMuted }}>
              Aucune entreprise trouvée
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingConfig;